// Action creator
// Actions
import { SetDefaultPlanAction } from "@ms/planner-actions/lib/local/appContext/SetContextPropertyActions";
import { FetchEntityCollectionSucceededAction, FetchEntitySucceededAction } from "@ms/planner-actions/lib/local/EntityActions";
import { FetchModernGroupPhotosSucceededAction } from "@ms/planner-actions/lib/local/modernGroup/ModernGroupFetchAction";
import { FetchMruItemsSucceededAction } from "@ms/planner-actions/lib/local/mru/MruActions";
import { FetchUserPhotoFailedAction, FetchUserPhotoSucceededAction } from "@ms/planner-actions/lib/local/user/UserFetchActions";
import { SetGanttZoomScaleAction, SetGridColumnWidthAction, SetSortCriteriaAction, SetTaskEditorActiveSidePanelAction, SetTaskEditorRenderModeAction, SetTaskEditorSidePanelWidthAction, SetTaskFilterAction, SetTaskGroupingStateAction, SetUnscheduledColumnVisibilityAction } from "@ms/planner-actions/lib/local/view/ViewActions";
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { CacheHydrationRequestType } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Utilities
import { isCustomFieldColumn, isCustomFieldFilter, isCustomFieldGrouping } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
// Models
import { EntityFilter } from "@ms/planner-models/lib/local/client/filter/Filter";
import { DefaultPlanSource } from "@ms/planner-models/lib/local/client/planner/DefaultPlanSource";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { GanttZoomViewTypes } from "@ms/planner-models/lib/local/client/view/IViewConfig";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/**
 * Action creator for fetching data from cache and hydrating the store
 */ export class CacheDataFetchActionCreator {
    async hydrateMruData(mruItems, onHydrated) {
        await this.dispatcher.dispatchActionAsync(new FetchMruItemsSucceededAction(mruItems));
        onHydrated();
    }
    async hydratePlannerDefaultPlanId(record) {
        await this.dispatcher.dispatchActionAsync(new SetDefaultPlanAction(record.planId, DefaultPlanSource.Planner));
    }
    hydrateUserPhotos(userPhotos) {
        if (userPhotos.length === 0) {
            return;
        }
        // Rehydrate each persisted user photo through the existing per-user fetch actions, carrying the
        // persisted status (including its original fetch timestamp) so fetch-time staleness evaluation
        // reflects the true last-fetch time. A null photo (status-only entry) represents a prior failed
        // fetch; restore it through the failed action so only its status is preserved. All actions are
        // batch-dispatched so the store publishes a single update for the whole rehydrated set.
        const actions = userPhotos.map(({ id, photo, status })=>photo !== null ? new FetchUserPhotoSucceededAction(id, photo, status) : new FetchUserPhotoFailedAction(id, null, "", "", status));
        this.dispatcher.batchDispatchActionsAsync(...actions);
    }
    hydrateModernGroupPhotos(modernGroupPhotos) {
        if (modernGroupPhotos.length === 0) {
            return;
        }
        // Rehydrate persisted modern group photos through the existing succeeded action, carrying a status
        // map (id -> persisted status, including its original fetch timestamp) so fetch-time staleness
        // evaluation reflects the true last-fetch time. Modern group photos always persist photo data (a
        // failed fetch stores an empty-string photo, not a null), so this mirrors the live-fetch shape.
        const photos = {};
        const statusMap = {};
        for (const { id, photo, status } of modernGroupPhotos){
            photos[id] = photo ?? "";
            statusMap[id] = status;
        }
        this.dispatcher.dispatchActionAsync(new FetchModernGroupPhotosSucceededAction(photos, statusMap));
    }
    async hydrateEntities(hydrations, onHydrated) {
        await Promise.all(hydrations.map((hydration)=>hydration.type === CacheHydrationRequestType.Single ? this.hydrateEntityData(hydration.entityType, hydration.data) : this.hydrateEntityCollectionData(hydration.entityType, hydration.relationship, hydration.parentId, hydration.data, hydration.deltaLink)));
        onHydrated();
    }
    async hydrateEntityData(entityType, data) {
        if (entityType === EntityType.Plan) {
            await this.storeCachedPlanData(data);
        } else if (entityType === EntityType.Task) {
            await this.storeCachedTaskData(data);
        } else {
            this.loggers.traceLogger.logTrace(0x1e10218b /* tag_4ecgl */ , TraceLevel.Warning, `Unsupported entity`);
            return;
        }
    }
    /** An unsupported entity/relationship pair is logged and dropped rather than throwing. */ async hydrateEntityCollectionData(entityType, relationship, parentId, data, deltaLink) {
        if (entityType === EntityType.Task) {
            switch(relationship){
                case QueryRelationship.TasksInMyDayOfUser:
                    await this.storeCachedMyDayTasksData(data, parentId, deltaLink);
                    break;
                case QueryRelationship.TasksAssignedToUser:
                    await this.storeCachedMyTasksData(data, parentId, deltaLink);
                    break;
                case QueryRelationship.TasksInPlan:
                    await this.storeCachedPlanTasksData(data, parentId, deltaLink);
                    break;
                default:
                    this.loggers.traceLogger.logTrace(0x1e10218e /* tag_4ecgo */ , TraceLevel.Warning, `Unsupported relationship for tasks`);
                    return;
            }
        } else if (entityType === EntityType.Bucket) {
            if (relationship !== QueryRelationship.BucketsInPlan) {
                this.loggers.traceLogger.logTrace(0x1e10218c /* tag_4ecgm */ , TraceLevel.Warning, `Unsupported relationship for Buckets`);
                return;
            }
            await this.storeCachedPlanBucketsData(data, parentId, deltaLink);
        } else if (entityType === EntityType.CustomFieldDefinition) {
            if (relationship !== QueryRelationship.CustomFieldDefinitionsInPlan) {
                this.loggers.traceLogger.logTrace(0x1d8997cb /* tag_38z5l */ , TraceLevel.Warning, `Unsupported relationship for custom fields`);
                return;
            }
            await this.storeCachedPlanCFDData(data, parentId, deltaLink);
        } else if (entityType === EntityType.CustomView) {
            if (relationship !== QueryRelationship.CustomViewsInPlan) {
                this.loggers.traceLogger.logTrace(0x1d75f494 /* tag_335su */ , TraceLevel.Warning, `Unsupported relationship for custom views`);
                return;
            }
            await this.storeCachedPlanCustomViewData(data, parentId, deltaLink);
        } else if (entityType === EntityType.ChatMessage) {
            if (relationship !== QueryRelationship.ChatMessagesForTask) {
                this.loggers.traceLogger.logTrace(0x1d85d6ce /* tag_3731o */ , TraceLevel.Warning, `Unsupported relationship for chat messages`);
                return;
            }
            await this.storeCachedTaskChatMessagesData(data, parentId, deltaLink);
        } else if (entityType === EntityType.Dependency) {
            if (relationship !== QueryRelationship.DependenciesInPlan) {
                this.loggers.traceLogger.logTrace(0x1d791047 /* tag_34rbh */ , TraceLevel.Warning, `Unsupported relationship for dependencies`);
                return;
            }
            await this.storeCachedPlanDependenciesData(data, parentId, deltaLink);
        } else {
            this.loggers.traceLogger.logTrace(0x1e10218d /* tag_4ecgn */ , TraceLevel.Warning, `Unsupported entity collection`);
            return;
        }
    }
    async hydrateViewConfig(viewId, configFromCache, onHydrated) {
        // Rollback safety: when the EnableCustomFields flight is off, custom field definitions are never fetched,
        // so a cached grouping/sort that references a custom field can no longer be resolved. Skip hydrating those
        // pieces of config (rather than applying broken state) so disabling the flight cleanly reverts the view.
        const enableCustomFields = this.configProvider().flights.EnableCustomFields;
        if (configFromCache.taskFilter !== null) {
            const effectiveFilter = this.deriveEffectiveTaskFilter(configFromCache.taskFilter, enableCustomFields);
            await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getCurrentTaskFilter(viewId) === null, new SetTaskFilterAction(viewId, effectiveFilter));
        }
        if (configFromCache.taskGrouping !== null) {
            if (!enableCustomFields && isCustomFieldGrouping(configFromCache.taskGrouping)) {
                this.loggers.traceLogger.logTrace(0x1d81c413 /* tag_362qt */ , TraceLevel.Warning, `Skipping cached custom field grouping hydration because EnableCustomFields is off`);
            } else {
                await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getCurrentTaskGroupingState(viewId, true) === TaskGroupingOption.None, new SetTaskGroupingStateAction(viewId, configFromCache.taskGrouping));
            }
        }
        if (configFromCache.sortCriteria !== null) {
            if (!enableCustomFields && isCustomFieldColumn(configFromCache.sortCriteria.primary.field)) {
                this.loggers.traceLogger.logTrace(0x1d81c412 /* tag_362qs */ , TraceLevel.Warning, `Skipping cached custom field sort hydration because EnableCustomFields is off`);
            } else {
                await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getSortCriteria(viewId) === null, new SetSortCriteriaAction(viewId, configFromCache.sortCriteria));
            }
        }
        if (configFromCache.gridColumnWidths !== null) {
            const effectiveGridColumnWidthEntries = this.getEffectiveGridColumnWidthEntries(configFromCache.gridColumnWidths, enableCustomFields);
            await Promise.all(effectiveGridColumnWidthEntries.map(([columnId, width])=>this.dispatcher.conditionalDispatchActionAsync(()=>!this.storeLoader.load(Store.View).getGridColumnWidths(viewId)?.[columnId], new SetGridColumnWidthAction(viewId, columnId, width))));
        }
        for (const ganttZoomViewType of GanttZoomViewTypes){
            const zoomScale = configFromCache.ganttZoomScaleByViewType?.[ganttZoomViewType];
            if (zoomScale != null) {
                await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getGanttZoomScale(viewId, ganttZoomViewType) === null, new SetGanttZoomScaleAction(viewId, zoomScale, ganttZoomViewType));
            }
        }
        if (configFromCache.isUnscheduledColumnVisible != null) {
            await this.dispatcher.conditionalDispatchActionAsync(()=>this.storeLoader.load(Store.View).getUnscheduledColumnVisibility(viewId) === null, new SetUnscheduledColumnVisibilityAction(viewId, configFromCache.isUnscheduledColumnVisible));
        }
        onHydrated();
    }
    async hydrateTaskEditorRenderMode(taskEditorRenderMode) {
        await this.dispatcher.dispatchActionAsync(new SetTaskEditorRenderModeAction(taskEditorRenderMode));
    }
    async hydrateTaskEditorActiveSidePanel(pane) {
        await this.dispatcher.dispatchActionAsync(new SetTaskEditorActiveSidePanelAction(pane));
    }
    async hydrateTaskEditorSidePanelWidth(width) {
        await this.dispatcher.dispatchActionAsync(new SetTaskEditorSidePanelWidthAction(width));
    }
    hydrateUsers(users) {
        if (users.length === 0) {
            return;
        }
        const actions = users.map(({ user, status })=>new FetchEntitySucceededAction(EntityType.User, user, undefined, status));
        this.dispatcher.batchDispatchActionsAsync(...actions);
    }
    /**
     * Derives the task filter to hydrate, dropping custom field filter keys when the EnableCustomFields flight is off.
     * When the flight is off, custom field definitions are never fetched, so a cached filter that references a custom
     * field can no longer be resolved; strip those keys rather than hydrating filters that can no longer be applied.
     * @param taskFilter The cached task filter from the view config
     * @param enableCustomFields Whether the EnableCustomFields flight is on
     */ deriveEffectiveTaskFilter(taskFilter, enableCustomFields) {
        if (!enableCustomFields) {
            const resolvableFilters = taskFilter.filters.filter((filter)=>!isCustomFieldFilter(filter.field));
            // Mirror the grouping/sort skip paths: leave a telemetry breadcrumb whenever custom field filter keys are
            // dropped, so a cached filter disappearing after the flight is turned off is diagnosable from traces.
            if (resolvableFilters.length !== taskFilter.filters.length) {
                this.loggers.traceLogger.logTrace(0x1d81c40b /* tag_362ql */ , TraceLevel.Warning, `Stripping cached custom field filter keys because EnableCustomFields is off`);
            }
            return new EntityFilter(resolvableFilters);
        }
        return taskFilter;
    }
    /**
     * Derives the grid column width entries to hydrate, dropping custom field column entries when the
     * EnableCustomFields flight is off. When the flight is off, custom field definitions are never fetched, so a
     * cached width keyed on a custom field column (cf_-prefixed) can no longer be resolved to a rendered column;
     * strip those entries rather than injecting widths for columns that can never appear.
     * @param gridColumnWidths The cached grid column widths from the view config
     * @param enableCustomFields Whether the EnableCustomFields flight is on
     */ getEffectiveGridColumnWidthEntries(gridColumnWidths, enableCustomFields) {
        const entries = Object.entries(gridColumnWidths);
        if (enableCustomFields) {
            return entries;
        }
        const resolvableEntries = entries.filter(([columnId])=>!isCustomFieldColumn(columnId));
        // Mirror the grouping/sort/filter skip paths: leave a telemetry breadcrumb whenever custom field column
        // widths are dropped, so a cached width disappearing after the flight is turned off is diagnosable from traces.
        if (resolvableEntries.length !== entries.length) {
            this.loggers.traceLogger.logTrace(0x1d8160cd /* tag_36wdn */ , TraceLevel.Warning, `Stripping cached custom field column widths because EnableCustomFields is off`);
        }
        return resolvableEntries;
    }
    storeCachedMyDayTasksData(tasks, parentId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Task, EntityType.User, parentId, QueryRelationship.TasksInMyDayOfUser, tasks, undefined /** nextLink */ , true, tasks.map((task)=>task.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getMyDayTasks()?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedMyTasksData(tasks, parentId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Task, EntityType.User, parentId, QueryRelationship.TasksAssignedToUser, tasks, undefined /** nextLink */ , true, tasks.map((task)=>task.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getTasksAssignedToUser(parentId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanData(plan) {
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Plan).getPlan(plan.id)?.status.state === StoreEntityState.Absent;
        }, new FetchEntitySucceededAction(EntityType.Plan, plan));
    }
    storeCachedTaskData(task) {
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getTask(task.id)?.status.state === StoreEntityState.Absent;
        }, new FetchEntitySucceededAction(EntityType.Task, task));
    }
    storeCachedPlanTasksData(tasks, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Task, EntityType.Plan, planId, QueryRelationship.TasksInPlan, tasks, undefined /** nextLink */ , true, tasks.map((task)=>task.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Tasks).getTasksForPlan(planId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanBucketsData(buckets, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Bucket, EntityType.Plan, planId, QueryRelationship.BucketsInPlan, buckets, undefined /** nextLink */ , true, buckets.map((bucket)=>bucket.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Bucket).getBucketsForPlan(planId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedTaskChatMessagesData(messages, taskId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.ChatMessage, EntityType.Task, taskId, QueryRelationship.ChatMessagesForTask, messages, undefined /** nextLink */ , true, messages.map((message)=>message.getUniqueId()), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Chat).getChatMessagesForTask(taskId)?.status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanCFDData(cfds, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.CustomFieldDefinition, EntityType.Plan, planId, QueryRelationship.CustomFieldDefinitionsInPlan, cfds, undefined /** nextLink */ , true, cfds.map((cfd)=>cfd.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.CustomFieldDefinition).getCustomFieldDefinitionsForPlan(planId).status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanCustomViewData(customViews, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.CustomView, EntityType.Plan, planId, QueryRelationship.CustomViewsInPlan, customViews, undefined /** nextLink */ , true, customViews.map((customView)=>customView.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.CustomView).getCustomViewsForPlan(planId).status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    storeCachedPlanDependenciesData(dependencies, planId, deltaLink) {
        const succeededAction = new FetchEntityCollectionSucceededAction(EntityType.Dependency, EntityType.Plan, planId, QueryRelationship.DependenciesInPlan, dependencies, undefined /** nextLink */ , true, dependencies.map((dependency)=>dependency.id), deltaLink);
        return this.dispatcher.conditionalDispatchActionAsync(()=>{
            return this.storeLoader.load(Store.Dependency).getDependenciesForPlan(planId).status.state === StoreEntityState.Absent;
        }, succeededAction);
    }
    /**
     * Initializes a new instance of the CacheDataFetchActionCreator.
     * @param storeLoader: Store loader
     * @param dispatcher: Dispatcher to dispatch actions
     * @param loggers: Logger context for telemetry
     * @param configProvider: Configuration provider, used to read flights (e.g. EnableCustomFields)
     */ constructor(storeLoader, dispatcher, loggers, configProvider){
        this.storeLoader = storeLoader;
        this.dispatcher = dispatcher;
        this.loggers = loggers;
        this.configProvider = configProvider;
    }
}
